import { Alert, Box, Card, Divider, Skeleton, Text } from '@nimbus-ds/components';
import { Layout, Page } from '@nimbus-ds/patterns';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { Route } from '../routes.js';

/**
 * Content-shaped loading skeletons — one per surface, each built from the SAME layout tokens as
 * the thing it stands in for.
 *
 * 🚨 **A skeleton's only job is to reserve the box the content will occupy.** What this file
 * replaces was one generic `LoadingState`: `n` full-width 72px bars, used for all eight loading
 * surfaces in the admin. It reserved the right shape for none of them — Analytics lands a
 * wrapping row of five 160px metric cards over three 260px trend cards, the Designer and the
 * offer editor land a two-column layout with a live preview in the right one, Quantity Offers
 * lands a toolbar over a stack of 56px rows, the shell landed the whole `AppShell` including a
 * sidebar the skeleton did not hint at. Every one of them jumped on load, which is the exact
 * thing a skeleton exists to prevent, and none of them told the merchant what was coming.
 *
 * Three rules keep these honest:
 *
 * 1. **Copy the real geometry, do not approximate it.** Where the content sets
 *    `flex="1 1 160px"` and `minWidth="150px"`, so does its skeleton. Those literals are
 *    asserted against the page sources in `skeletons.test.tsx`, because a skeleton that has
 *    drifted from its content is worse than no skeleton: it reserves confidently wrong space.
 * 2. **Text bars are drawn on the Nimbus type scale** (`TYPE` below, read from
 *    `@nimbus-ds/styles`), so a caption reserves a 16px line box and an `h3` reserves 28px.
 *    Sizing text bars by eye is how a card ends up 40px short of what lands in it.
 * 3. **Reserve geometry, never colour.** The dashboard's revenue panel is `success-surface` and
 *    the tracking warning is `warning-surface`; their skeletons are neutral. A loading state
 *    must not assert a value — "you earned money", "something is wrong" — that it does not have
 *    yet. The tint arrives with the data.
 *
 * 4. **Do not skeleton what is already known.** A block of fixed copy — the tracking page's
 *    duplicate-tracking warning — has a height set by how that copy wraps, which changes with the
 *    locale and the viewport; any number baked in is right for one of each. Rendering the real
 *    block is exact everywhere, costs nothing, and lets the merchant read it while the rest loads.
 *
 * Where a page's real height genuinely cannot be known before the fetch (does this store have
 * recent orders? is the checklist collapsed?), the skeleton reserves the COMMON case and the
 * comment says which way it errs. Under-reserving at the bottom of a page is cheap; over-
 * reserving above the fold pushes content the merchant is already reading.
 */

/**
 * Line-box and glyph height, in px, of each step on the Nimbus type scale — `fontSize-body-*` /
 * `fontSize-title-h*` over `lineWeight-*`, read from `@nimbus-ds/styles`.
 *
 * The bar is drawn at the GLYPH height and centred in the LINE box, so it reads as a line of
 * text rather than a solid slab while still reserving exactly the height the text will take.
 */
const TYPE = {
  /**
   * `<Text fontSize="caption">` — 0.75rem glyphs.
   *
   * 🚨 In a **20px** line box, not the 16px `lineWeight-body-caption` suggests. Nimbus `Text`
   * takes `fontSize` and `lineHeight` as separate props and defaults `lineHeight="base"`; no
   * caller in this app passes `lineHeight` at all, so caption copy still sits on the body line.
   * Measured in Chrome, which is also how the 4px-per-card shortfall this used to cause was
   * found. `skeletons.test.tsx` pins it to `lineWeight-body-base` for that reason.
   */
  caption: { line: 20, glyph: 12 },
  /** `<Text>` — 0.875rem over 1.25rem. */
  base: { line: 20, glyph: 14 },
  /** `<Title as="h5">` — 1rem over 1.5rem. */
  h5: { line: 24, glyph: 16 },
  /** `<Title as="h4">`, and the title a `Card.Header` renders — 1.25rem over 1.5rem. */
  h4: { line: 24, glyph: 20 },
  /** `<Title as="h3">` — 1.5rem over 1.75rem. */
  h3: { line: 28, glyph: 24 },
  /** `<Title as="h2">` — 1.75rem over 2rem. */
  h2: { line: 32, glyph: 28 },
} as const;

export type TextScale = keyof typeof TYPE;

/** Heights of the Nimbus controls the skeletons stand in for, from `@nimbus-ds/styles`. */
const CONTROL = {
  /** `Button` and `IconButton size="2rem"` — 2rem, and `box-sizing: border-box`. */
  button: '32px',
  /**
   * `Input` / `Select` / `FormField.Input` — 2rem of control plus the container's 1px border top
   * and bottom, which is NOT inside the box. Measured at 34px; assuming 32 left every field
   * skeleton 2px short.
   */
  input: '34px',
  /** `Toggle`'s slider is 2rem x 1rem. */
  toggleW: '32px',
  toggleH: '16px',
} as const;

/**
 * One line of text.
 *
 * `width` is a percentage of the container in most places, because real copy is ragged and a
 * skeleton that spans the full width reads as a block, not a line.
 */
export function TextSkeleton({
  width,
  scale = 'base',
}: {
  width: string;
  scale?: TextScale;
}): JSX.Element {
  const { line, glyph } = TYPE[scale];
  return (
    <Box display="flex" alignItems="center" height={`${line}px`} width={width} minWidth="0">
      <Skeleton width="100%" height={`${glyph}px`} borderRadius="4px" />
    </Box>
  );
}

/** A control- or panel-sized rectangle: buttons, inputs, charts, preview panels. */
export function BlockSkeleton({
  width,
  height,
  radius = '8px',
}: {
  width: string;
  height: string;
  /** `8px` is `shape-border-radius-2`, what Nimbus gives buttons and cards. */
  radius?: string;
}): JSX.Element {
  return <Skeleton width={width} height={height} borderRadius={radius} />;
}

/** A `Tag`/pill: fully rounded, one caption line tall plus its padding. */
function PillSkeleton({ width }: { width: string }): JSX.Element {
  return <Skeleton width={width} height="20px" borderRadius="9999px" />;
}

/** A `Toggle`. */
function ToggleSkeleton(): JSX.Element {
  return <Skeleton width={CONTROL.toggleW} height={CONTROL.toggleH} borderRadius="9999px" />;
}

/** A `Button`, sized by its caption. */
function ButtonSkeleton({ width }: { width: string }): JSX.Element {
  return <BlockSkeleton width={width} height={CONTROL.button} />;
}

/**
 * The right-aligned action row every page except the Dashboard opens with (Save/Discard, Add…).
 *
 * `marginBottom="4"` matches the real bars in `TrackingPixels`, `ShippingRates` and
 * `QuantityOffers`, so the first card below starts at the same y.
 */
function ActionBarSkeleton({ widths }: { widths: string[] }): JSX.Element {
  return (
    <Box
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      gap="2"
      marginBottom="4"
      height={CONTROL.button}
    >
      {widths.map((width, i) => (
        <ButtonSkeleton key={i} width={width} />
      ))}
    </Box>
  );
}

/** A labelled input — `FormField.Input`, which is what `TextControl`/`SelectControl` render. */
function FieldSkeleton({ label = '35%' }: { label?: string }): JSX.Element {
  return (
    <Box display="flex" flexDirection="column" gap="1">
      <TextSkeleton width={label} />
      <BlockSkeleton width="100%" height={CONTROL.input} />
    </Box>
  );
}

/* ───────────────────────── Analytics ───────────────────────── */

/**
 * `Analytics`'s `StatCard`: a caption label over an `h3` value, in a card that takes an equal
 * share of a wrapping row. The flex tokens are copied verbatim from `StatCard` — see rule 1.
 */
export function StatCardSkeleton(): JSX.Element {
  return (
    <Box flex="1 1 160px" minWidth="150px">
      <Card>
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="1">
            <TextSkeleton width="65%" scale="caption" />
            <TextSkeleton width="45%" scale="h3" />
          </Box>
        </Card.Body>
      </Card>
    </Box>
  );
}

/**
 * The plotted height of `TrendChart`, derived rather than guessed: `PAD_TOP` 10 + `PLOT_H` 104 +
 * `TICK_GAP` 6 + the rotated x-labels + 2. Labels are `MM-DD` (`shortDayLabel`), so their
 * footprint is `ceil(5 * AXIS_CHAR_W(6) * 0.71) + FONT(10)` = 32.
 *
 * 🚨 It deliberately reserves the PLOTTED height, not the 72px empty-state box: a range with no
 * data collapses to the smaller one, and shrinking after load costs nothing, while a chart that
 * grows 80px shoves the offers card down the screen.
 */
const TREND_CHART_HEIGHT = '154px';

/** `Analytics`'s `TrendCard`: caption, `h4` value, chart. */
export function TrendCardSkeleton(): JSX.Element {
  return (
    <Box flex="1 1 260px" minWidth="240px">
      <Card>
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="2">
            <Box display="flex" flexDirection="column" gap="1">
              <TextSkeleton width="50%" scale="caption" />
              <TextSkeleton width="35%" scale="h4" />
            </Box>
            <BlockSkeleton width="100%" height={TREND_CHART_HEIGHT} />
          </Box>
        </Card.Body>
      </Card>
    </Box>
  );
}

/**
 * Everything `Analytics` gates on its panel fetch: five metric cards, three trend cards, and the
 * quantity-offers card with three more metric cards inside it. Exactly the tree the page builds
 * when `panel` lands.
 */
export function AnalyticsPanelSkeleton(): JSX.Element {
  return (
    <>
      <Box display="flex" flexWrap="wrap" gap="4">
        {[0, 1, 2, 3, 4].map((i) => (
          <StatCardSkeleton key={i} />
        ))}
      </Box>
      <Box display="flex" flexWrap="wrap" gap="4">
        {[0, 1, 2].map((i) => (
          <TrendCardSkeleton key={i} />
        ))}
      </Box>
      <Card>
        <Card.Header>
          <TextSkeleton width="40%" scale="h4" />
        </Card.Header>
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="3">
            <Box display="flex" flexWrap="wrap" gap="4">
              {[0, 1, 2].map((i) => (
                <StatCardSkeleton key={i} />
              ))}
            </Box>
          </Box>
        </Card.Body>
      </Card>
    </>
  );
}

/** The whole Analytics page, for the shell-level load: date-range card, then the panel. */
function AnalyticsPageSkeleton(): JSX.Element {
  return (
    <Page maxWidth="1200px">
      <Page.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Card>
            <Card.Body>
              <Box display="flex" flexWrap="wrap" gap="4" alignItems="flex-end">
                <Box flex="1 1 220px" minWidth="200px">
                  <FieldSkeleton label="45%" />
                </Box>
              </Box>
            </Card.Body>
          </Card>
          <AnalyticsPanelSkeleton />
        </Box>
      </Page.Body>
    </Page>
  );
}

/* ───────────────────────── Dashboard ───────────────────────── */

/**
 * The setup checklist, which the Dashboard hides behind its order fetch so the progress count
 * cannot be watched correcting itself from "5 of 6" to "6 of 6".
 *
 * `steps` is passed in rather than hard-coded so the skeleton always draws exactly the rows the
 * page is about to draw. It reserves the OPEN body: while loading, the `order` step is
 * necessarily unfinished, so `showChecklistBody` is true for every store that is not already
 * fully set up — and a fully set-up store's card collapsing after load shrinks the page, which
 * is the cheap direction.
 */
export function ChecklistSkeleton({ steps }: { steps: number }): JSX.Element {
  return (
    <Card>
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="3">
          <Box display="flex" alignItems="center" justifyContent="space-between" gap="2">
            <Box display="flex" flexDirection="column" gap="1" flex="1" minWidth="0">
              <TextSkeleton width="45%" scale="h4" />
              <TextSkeleton width="25%" scale="caption" />
            </Box>
            <ButtonSkeleton width="104px" />
          </Box>
          <Box display="flex" flexDirection="column">
            {Array.from({ length: steps }, (_, i) => (
              <Fragment key={i}>
                {i > 0 && <Divider />}
                <Box display="flex" alignItems="center" gap="3" paddingY="3">
                  {/* The step's CheckCircleIcon, `size="medium"`. */}
                  <Skeleton width="24px" height="24px" borderRadius="9999px" />
                  <Box display="flex" flexDirection="column" gap="1" flex="1" minWidth="0">
                    <TextSkeleton width="45%" />
                    <TextSkeleton width="70%" scale="caption" />
                  </Box>
                  <BlockSkeleton width="88px" height="16px" radius="4px" />
                </Box>
              </Fragment>
            ))}
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

/**
 * The lifetime funnel card: an `h4` title, the three-stage chain with its two conversion links,
 * and the revenue panel.
 *
 * Deliberately does NOT reserve the "Recent orders" card below it. Whether that card exists at
 * all depends on the fetch (`recentOrders.length > 0`), and so does whether this whole region is
 * replaced by the "no activity yet" empty state — reserving for a card that may never come would
 * leave a hole on every new store, and it sits at the bottom of the page where growth shifts
 * nothing above it.
 */
export function FunnelSkeleton(): JSX.Element {
  return (
    <Card>
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <TextSkeleton width="35%" scale="h4" />
          <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap="3">
            {[0, 1, 2].map((i) => (
              <Fragment key={i}>
                {i > 0 && (
                  /* FunnelLink: an equal share of the row, pill + arrow, centred. */
                  <Box
                    flex="1 1 0%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap="2"
                    paddingTop="1"
                  >
                    <PillSkeleton width="48px" />
                    <Skeleton width="12px" height="12px" borderRadius="4px" />
                  </Box>
                )}
                <Box
                  flex="1 1 0%"
                  minWidth="80px"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap="1"
                >
                  <TextSkeleton width="60%" scale="h3" />
                  <TextSkeleton width="85%" scale="caption" />
                </Box>
              </Fragment>
            ))}
          </Box>
          {/* The revenue panel. Neutral, not `success-surface`: see rule 3. */}
          <Box
            backgroundColor="neutral-surface"
            borderRadius="2"
            padding="4"
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap="2"
          >
            <Box display="flex" flexDirection="column" gap="1" flex="1" minWidth="0">
              <TextSkeleton width="40%" scale="caption" />
              <TextSkeleton width="55%" scale="h2" />
            </Box>
            <PillSkeleton width="128px" />
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

/**
 * One "what's live right now" card. Mirrors `CardSurface` + `LiveCard`, which is a `Box` and not
 * a `Card` for the stretch reasons documented there — so this is a `Box` too, with the same
 * tokens, or the row of five would not line up.
 */
function LiveCardSkeleton(): JSX.Element {
  return (
    <Box
      flex="1 1 170px"
      minWidth="160px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      gap="2"
      padding="4"
      backgroundColor="neutral-background"
      borderRadius="2"
      boxShadow="2"
    >
      <Box display="flex" flexDirection="column" gap="1">
        <TextSkeleton width="70%" scale="caption" />
        <TextSkeleton width="55%" scale="h4" />
      </Box>
      <Box display="flex">
        <BlockSkeleton width="72px" height="16px" radius="4px" />
      </Box>
    </Box>
  );
}

/**
 * The whole Dashboard, for the shell-level load.
 *
 * The welcome banner is not reserved: it only renders while `welcome_dismissed` is false, which
 * is unknowable before the fetch, and it is the FIRST thing on the page — reserving a card that
 * may not arrive would leave a gap at the top for every merchant who has dismissed it, which is
 * most of them after day one.
 *
 * Five live cards, because that is what a store with the form switched on gets; a store with
 * `visibility: 'disabled'` drops the mode card and the row reflows one card shorter.
 */
function DashboardPageSkeleton(): JSX.Element {
  return (
    <Page maxWidth="1000px">
      <Page.Body>
        <Box display="flex" flexDirection="column" gap="4">
          {/* App status card: title + help caption, toggle pinned right. */}
          <Card>
            <Card.Body>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap="2">
                <Box display="flex" flexDirection="column" gap="1" flex="1" minWidth="0">
                  <TextSkeleton width="40%" scale="h4" />
                  <TextSkeleton width="65%" scale="caption" />
                </Box>
                <ToggleSkeleton />
              </Box>
            </Card.Body>
          </Card>

          <ChecklistSkeleton steps={6} />

          <Box display="flex" flexDirection="column" gap="2">
            <TextSkeleton width="30%" scale="h4" />
            <Box display="flex" flexWrap="wrap" gap="4">
              {[0, 1, 2, 3, 4].map((i) => (
                <LiveCardSkeleton key={i} />
              ))}
            </Box>
          </Box>

          <FunnelSkeleton />
        </Box>
      </Page.Body>
    </Page>
  );
}

/* ───────────────────────── Form Designer / offer editor ───────────────────────── */

/**
 * The live preview column's reserved height.
 *
 * ⚠️ The one height here that cannot be derived: the preview renders the merchant's ACTUAL form,
 * so it is as tall as however many fields they have configured — routinely more than a screen.
 *
 * Viewport-relative rather than a fixed guess, because that is what the real column resolves to:
 * `columnScroll` caps the wrapper at `calc(100vh - 120px)` and scrolls it, so anything past that
 * is behind a scrollbar and a taller reservation would buy nothing. The extra 120px is the card's
 * own chrome — header, the card's `gap-4`, and two 16px paddings — leaving the block filling the
 * visible column exactly as a real form does.
 */
const PREVIEW_HEIGHT = 'calc(100vh - 240px)';

/** The right-hand preview column, shared by the Designer and the offer editor. */
function PreviewColumnSkeleton(): JSX.Element {
  return (
    <Card>
      <Card.Header>
        <TextSkeleton width="35%" scale="h4" />
      </Card.Header>
      <Card.Body>
        <Box backgroundColor="neutral-surface" padding="4" borderRadius="2">
          <BlockSkeleton width="100%" height={PREVIEW_HEIGHT} />
        </Box>
      </Card.Body>
    </Card>
  );
}

/**
 * `FormDesigner`: the sticky Save/Discard bar, then the two-column layout — mode switch and the
 * tab card on the left, live preview on the right.
 *
 * `Layout columns="2 - symmetric"` and `minWidth="0"` on both sections are copied from the page
 * for the reason its own comment gives: without `minWidth="0"` a `1fr` track is floored at its
 * content's min-content width and the whole page scrolls sideways on a phone. A skeleton that
 * dropped it would introduce that bug for the duration of the load.
 */
export function DesignerSkeleton(): JSX.Element {
  return (
    <Page maxWidth="1200px">
      <Page.Body>
        <Box display="flex" justifyContent="flex-end" gap="2" paddingTop="3" paddingBottom="3">
          <ButtonSkeleton width="88px" />
          <ButtonSkeleton width="88px" />
        </Box>
        <Layout columns="2 - symmetric">
          <Layout.Section minWidth="0">
            {/* ModeSwitch: two option cards side by side, then the info banner below them. */}
            <Card>
              <Card.Body>
                <Box display="flex" gap="4">
                  <Box flex="1">
                    <BlockSkeleton width="100%" height="105px" />
                  </Box>
                  <Box flex="1">
                    <BlockSkeleton width="100%" height="105px" />
                  </Box>
                </Box>
                <Box marginTop="3">
                  <BlockSkeleton width="100%" height="56px" />
                </Box>
              </Card.Body>
            </Card>
            <Box marginTop="4">
              <Card>
                <Card.Body>
                  {/* The tab strip, then the selected tab's controls. */}
                  <Box display="flex" gap="4" alignItems="center">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <TextSkeleton key={i} width="64px" />
                    ))}
                  </Box>
                  <Box marginTop="4" display="flex" flexDirection="column" gap="4">
                    {[0, 1, 2, 3].map((i) => (
                      <FieldSkeleton key={i} />
                    ))}
                  </Box>
                </Card.Body>
              </Card>
            </Box>
          </Layout.Section>
          <Layout.Section minWidth="0">
            <PreviewColumnSkeleton />
          </Layout.Section>
        </Layout>
      </Page.Body>
    </Page>
  );
}

/**
 * `QuantityOfferEditor`: its sticky header (back, title, on/off pill, Save), then the same
 * two-column split as the Designer with the offer's cards on the left.
 */
export function OfferEditorSkeleton(): JSX.Element {
  return (
    <Page maxWidth="1200px">
      <Page.Body>
        <Box paddingX="4" paddingY="3" marginBottom="4" display="flex" alignItems="center" gap="2">
          <Skeleton width="32px" height="32px" borderRadius="8px" />
          <TextSkeleton width="180px" scale="h3" />
          <Box flex="1" />
          <ToggleSkeleton />
          <TextSkeleton width="56px" />
        </Box>
        <Layout columns="2 - symmetric">
          <Layout.Section minWidth="0">
            <Box display="flex" flexDirection="column" gap="4">
              <TextSkeleton width="30%" />
              <Card>
                <Card.Body>
                  <FieldSkeleton />
                </Card.Body>
              </Card>
              <Card>
                <Card.Body>
                  <Box display="flex" flexDirection="column" gap="5">
                    <TextSkeleton width="35%" />
                    <ButtonSkeleton width="152px" />
                  </Box>
                </Card.Body>
              </Card>
              <Card>
                <Card.Body>
                  <Box display="flex" flexDirection="column" gap="4">
                    <TextSkeleton width="30%" />
                    <BlockSkeleton width="100%" height="64px" />
                    <BlockSkeleton width="100%" height="64px" />
                    <ButtonSkeleton width="128px" />
                  </Box>
                </Card.Body>
              </Card>
            </Box>
          </Layout.Section>
          <Layout.Section minWidth="0">
            <PreviewColumnSkeleton />
          </Layout.Section>
        </Layout>
      </Page.Body>
    </Page>
  );
}

/* ───────────────────────── List pages ───────────────────────── */

/**
 * A `QuantityOffers` row: drag handle, priority, toggle, name, three icon actions. The column
 * widths (48px / 80px) and the `display={{ xs: 'none', md: 'block' }}` on the name are copied
 * from the row, so the skeleton stacks on a phone exactly where the row does.
 */
function OfferRowSkeleton(): JSX.Element {
  return (
    <Card>
      <Card.Body>
        <Box display={{ xs: 'block', md: 'none' }} marginBottom="2" minWidth="0">
          <TextSkeleton width="55%" />
        </Box>
        <Box display="flex" alignItems="center" gap="2">
          <Skeleton width="16px" height="16px" borderRadius="4px" />
          <Box width="48px">
            <TextSkeleton width="16px" />
          </Box>
          <Box width="80px">
            <ToggleSkeleton />
          </Box>
          <Box flex="1" display={{ xs: 'none', md: 'block' }} minWidth="0">
            <TextSkeleton width="55%" />
          </Box>
          <Box display="flex" gap="1" flex="0 0 auto" marginLeft={{ xs: 'auto', md: 'none' }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} width="32px" height="32px" borderRadius="8px" />
            ))}
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

/**
 * `QuantityOffers`: the search + actions toolbar, the desktop-only column header, and three
 * rows. Three because the list is paginated at a size well above that and a merchant with any
 * offers at all usually has a handful — it under-reserves for a long list, which grows the page
 * downward from a point below anything being read.
 */
export function OfferListSkeleton(): JSX.Element {
  return (
    <Page maxWidth="1000px">
      <Page.Body>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="2"
          marginBottom="4"
          flexWrap={{ xs: 'wrap', md: 'nowrap' }}
        >
          <Box display="flex" gap="2" flex={{ xs: '1 1 100%', md: '1' }} maxWidth="520px">
            <Box flex="1">
              <BlockSkeleton width="100%" height={CONTROL.input} />
            </Box>
            <ButtonSkeleton width="104px" />
          </Box>
          <Box display="flex" alignItems="center" gap="2" flex="0 0 auto" marginLeft="auto">
            <ButtonSkeleton width="120px" />
          </Box>
        </Box>
        <Box
          display={{ xs: 'none', md: 'flex' }}
          alignItems="center"
          gap="2"
          paddingX="4"
          paddingY="2"
        >
          <Box width="80px">
            <TextSkeleton width="70%" />
          </Box>
          <Box width="80px">
            <TextSkeleton width="70%" />
          </Box>
          <Box flex="1">
            <TextSkeleton width="20%" />
          </Box>
          <TextSkeleton width="64px" />
        </Box>
        <Box display="flex" flexDirection="column" gap="2">
          {[0, 1, 2].map((i) => (
            <OfferRowSkeleton key={i} />
          ))}
        </Box>
      </Page.Body>
    </Page>
  );
}

/** `ShippingRates`: the Add-rate bar, the desktop-only column header, three rate rows. */
function ShippingRatesSkeleton(): JSX.Element {
  return (
    <Page maxWidth="1000px">
      <Page.Body>
        <ActionBarSkeleton widths={['112px']} />
        <Box
          display={{ xs: 'none', md: 'flex' }}
          alignItems="center"
          gap="2"
          paddingX="4"
          paddingY="2"
        >
          <Box flex="1">
            <TextSkeleton width="20%" />
          </Box>
          <Box width="100px">
            <TextSkeleton width="60%" />
          </Box>
          <Box flex="1">
            <TextSkeleton width="30%" />
          </Box>
          <Box width="80px">
            <TextSkeleton width="70%" />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap="2">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <Box
                display="flex"
                alignItems="center"
                gap="2"
                flexWrap={{ xs: 'wrap', md: 'nowrap' }}
              >
                <Box flex={{ xs: '1 1 100%', md: '1' }} minWidth="0">
                  <TextSkeleton width="55%" />
                </Box>
                <Box width={{ xs: 'auto', md: '100px' }}>
                  <TextSkeleton width="56px" />
                </Box>
                <Box flex="1" minWidth="0">
                  <TextSkeleton width="70%" scale="caption" />
                </Box>
                <Box display="flex" gap="1" flex="0 0 auto" marginLeft={{ xs: 'auto', md: 'none' }}>
                  <Skeleton width="32px" height="32px" borderRadius="8px" />
                  <Skeleton width="32px" height="32px" borderRadius="8px" />
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      </Page.Body>
    </Page>
  );
}

/**
 * `TrackingPixels`: the Add/Save bar, the duplicate-tracking warning, the master switch, and
 * three collapsed pixel sections.
 *
 * The sections are drawn COLLAPSED — `CollapsibleSection` opens only the one the merchant just
 * added (`defaultOpen={p.id === openId}`), and on a page load nothing is open — so the header
 * row is the whole card.
 */
export function TrackingSkeleton(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Page maxWidth="900px">
      <Page.Body>
        <ActionBarSkeleton widths={['104px', '88px']} />
        <Box display="flex" flexDirection="column" gap="4">
          {/* 🚨 The REAL alert and the REAL label, not bars.
              Rule 4, and this is the only page that needs it: both are fixed copy that the fetch
              cannot change, and their height is set by how that copy WRAPS — 118px in Spanish at
              900px, less in English, more on a phone. Any number baked in here would be right for
              exactly one locale at one width. Rendering the real thing is exact everywhere, and
              the merchant gets to read the warning while the pixels load. Only the switch's
              position is unknown, so only the switch is a skeleton. */}
          <Alert appearance="warning" title={t('tracking.dupWarningTitle')}>
            <Text>{t('tracking.dupWarningBody')}</Text>
          </Alert>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap="2">
            <Text>{t('tracking.masterEnable')}</Text>
            <ToggleSkeleton />
          </Box>
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap="2">
                <Box minWidth="0" flex="1">
                  <TextSkeleton width="55%" scale="h5" />
                </Box>
                <Box display="flex" alignItems="center" gap="2" flex="0 0 auto">
                  <PillSkeleton width="72px" />
                  <Skeleton width="16px" height="16px" borderRadius="4px" />
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      </Page.Body>
    </Page>
  );
}

/**
 * `Settings`: the visibility card, the cart-mode card and the thank-you card, each a
 * `Card.Header` over a heading, a caption and a 44px `Segmented` control (4px padding + a 33px
 * button + 4px + the 1px border, measured from its own styles).
 */
function SettingsSkeleton(): JSX.Element {
  return (
    <Page maxWidth="800px">
      <Page.Body>
        <Box display="flex" flexDirection="column" gap="4">
          {/* Visibility, then cart mode. Only the first carries an info banner under its
              control; both were measured in Chrome (238px and 198px at 800px in Spanish). */}
          {[true, false].map((banner) => (
            <Card key={String(banner)}>
              <Card.Header>
                <TextSkeleton width="35%" scale="h4" />
              </Card.Header>
              <Card.Body>
                <Box display="flex" flexDirection="column" gap="4">
                  <Box display="flex" flexDirection="column" gap="1">
                    <TextSkeleton width="45%" scale="h5" />
                    <TextSkeleton width="75%" scale="caption" />
                  </Box>
                  {/* `Segmented`: 4px padding + a 32px button + 4px + a 1px border, = 42. */}
                  <BlockSkeleton width="100%" height="42px" radius="10px" />
                  {banner && <BlockSkeleton width="100%" height="44px" />}
                </Box>
              </Card.Body>
            </Card>
          ))}
          <Card>
            <Card.Header>
              <TextSkeleton width="40%" scale="h4" />
            </Card.Header>
            <Card.Body>
              <Box display="flex" flexDirection="column" gap="4">
                <FieldSkeleton />
                <FieldSkeleton />
              </Box>
            </Card.Body>
          </Card>
        </Box>
      </Page.Body>
    </Page>
  );
}

/* ───────────────────────── Shell ───────────────────────── */

/**
 * The skeleton for whichever page the shell is about to render.
 *
 * 🚨 It is the ROUTE that picks the shape, because the shell's initial fetch resolves onto a
 * specific page — `pathToRoute(window.location.pathname)` has already decided which. A single
 * generic skeleton here was the biggest jump in the app: the merchant deep-linked to Analytics
 * got four grey bars and then a grid of eleven cards.
 */
export function RouteSkeleton({ route }: { route: Route }): JSX.Element {
  switch (route) {
    case 'analytics':
      return <AnalyticsPageSkeleton />;
    case 'designer':
      return <DesignerSkeleton />;
    case 'shipping-rates':
      return <ShippingRatesSkeleton />;
    case 'quantity-offers':
      return <OfferListSkeleton />;
    case 'tracking':
      return <TrackingSkeleton />;
    case 'settings':
      return <SettingsSkeleton />;
    case 'dashboard':
      return <DashboardPageSkeleton />;
  }
}
